import type { DicomInfo } from '@/lib/dicom/DicomInfo';
import type { DicomGroups, DicomSeries } from '@/lib/dicom/DicomSeries';
import DicomUtils from '@/lib/dicom/DicomUtil';
import { logger } from '@/util/logger';
import { errorMessage } from '@/util/errorMessage';
import type { ImageModality } from '@/api/study/study';

const log = logger('validateDicoms');

export interface DicomValidatedResultOutput {
    series: DicomSeries;
    id: string;
}

export interface ProcessDicomsResult {
    input: DicomGroups;
    uploadCandidates: DicomValidatedResultOutput[]; // 0 if invalid; 1+ if valid
    isValid: boolean;
}

export interface ValidateDicomsOptions {
    requireSingleSeries: boolean;
}

export async function validateDicoms(
    input: DicomInfo[],
    modalityType: ImageModality = 'ct-scan',
    options: ValidateDicomsOptions = { requireSingleSeries: true },
): Promise<ProcessDicomsResult> {
    try {
        const validatedGroups: Record<string, DicomSeries> =
            await DicomUtils.makeValidatedDicomSeries(input, modalityType);
        const dicomSeries = Object.entries(validatedGroups);

        const candidates: DicomValidatedResultOutput[] = dicomSeries
            .filter(([, group]) => !group.isExcluded)
            .map(([id, series]) => ({ id, series }));

        if (candidates.length === 0) {
            log.error('No valid candidate series found for upload.');
            return { input: validatedGroups, uploadCandidates: [], isValid: false };
        }

        if (options.requireSingleSeries && candidates.length > 1) {
            log.error('Finding one series failed. Got %d, %o', candidates.length, candidates);
            return { input: validatedGroups, uploadCandidates: [], isValid: false };
        }

        return { input: validatedGroups, uploadCandidates: candidates, isValid: true };
    } catch (error: unknown) {
        log.error('Error when validating Dicoms: %s', errorMessage(error));
    }

    return { input: {}, uploadCandidates: [], isValid: false };
}
